import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import FileViewer from './FileViewer';
import { supabase } from '../lib/supabaseClient';

const LibraryPreviewModal = ({ item, onClose, onComplete }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(true);
  const [errorUrl, setErrorUrl] = useState(false);

  useEffect(() => {
    if (!item) return;

    let isMounted = true;
    setLoadingUrl(true);
    setErrorUrl(false);
    setPreviewUrl(null);

    const fetchUrl = async () => {
      try {
        // Se for um link externo, usa a URL direta
        if (item.type === 'Link' && item.url) {
          if (isMounted) {
            setPreviewUrl(item.url);
            setLoadingUrl(false);
          }
          return;
        }

        // Se tiver file_path, gera URL assinada do Supabase
        if (item.file_path) {
          const { data, error } = await supabase.storage
            .from('library-contents')
            .createSignedUrl(item.file_path, 3600); // 1 hora de expiração

          if (error) throw error;
          
          if (isMounted && data?.signedUrl) {
            setPreviewUrl(data.signedUrl);
            setLoadingUrl(false);
          }
          return;
        }

        // Fallback: se tiver url no item (legado ou outro caso), tenta usar
        if (item.url) {
          if (isMounted) {
            setPreviewUrl(item.url);
            setLoadingUrl(false);
          }
          return;
        }

        // Se não tiver nada
        throw new Error('Nenhuma URL ou arquivo encontrado para visualização.');
      } catch (err) {
        console.error('Erro ao gerar URL de preview:', err);
        if (isMounted) {
          setErrorUrl(true);
          setLoadingUrl(false);
        }
      }
    };

    fetchUrl();

    return () => {
      isMounted = false;
    };
  }, [item]);

  if (!item) return null;

  const allowHtmlPdfDownload = Boolean(
    String(item?.type || '').toLowerCase() === 'html' &&
    (item?.meta?.allowPdfDownload ?? item?.meta?.allow_pdf_download)
  );

  return (
    <div className="fixed inset-0 top-16 z-[900] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4 animate-fade-in">
      <div 
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl h-full max-h-[calc(100vh-6rem)] shadow-2xl flex flex-col relative overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900/50 backdrop-blur-md z-10">
          <div className="flex flex-col gap-0.5 overflow-hidden">
            <h3 id="preview-title" className="text-lg font-bold text-white truncate pr-4">
              {item.title}
            </h3>
            <p className="text-xs text-slate-400 truncate">
              {item.originalFileName || item.original_filename || item.type}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/50"
            aria-label="Fechar visualização"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 w-full min-h-0 bg-slate-950 relative">
          {loadingUrl ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Loader2 size={40} className="text-teal-500 animate-spin mb-4" />
              <p className="text-slate-400 font-medium">Preparando visualização...</p>
            </div>
          ) : errorUrl ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4">
                <X size={32} className="text-rose-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Erro ao carregar arquivo</h3>
              <p className="text-slate-400 max-w-md">
                Não foi possível gerar o link de visualização. O arquivo pode ter sido movido ou você não tem permissão para acessá-lo.
              </p>
            </div>
          ) : (
            <FileViewer
              url={previewUrl}
              title={item.title}
              type={item.type}
              description={item.description}
              allowHtmlPdfDownload={allowHtmlPdfDownload}
              onComplete={onComplete} 
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryPreviewModal;
